import { cloneDeep, differenceWith, find, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  Company,
  CreateCompanyAction,
  CustomerData,
  DeleteCompanyAction,
  FetchCompaniesAction,
  ReduxStoreData,
  ServerCompanyCreate,
  ServerCompanyUpdate,
  StoreApplicant,
  UpdateCompanyAction,
  UpsertCompaniesAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import { waitForAuth } from '../_common';
import { updateEmploymentsAbort } from '../employments/actions';
import { createIncomeAbort } from '../incomes/actions';
import {
  createCompany,
  createCompanyFail,
  createCompanySuccess,
  deleteCompanyFail,
  deleteCompanySuccess,
  fetchCompaniesFail,
  fetchCompaniesSuccess,
  updateCompany,
  updateCompanyFail,
  updateCompanySuccess,
  upsertCompaniesFail,
  upsertCompaniesSuccess,
} from './actions';
import CompanyAPI from './api';
import { addCompanyDefaultOwners, fixCompanyOwnership, getCompanyId } from './utils';

export function* createCompanyWorker(action: CreateCompanyAction): any {
  try {
    yield waitForAuth();
    const {
      applicationId,
      company: { clientId },
    } = action;

    const company = omit(action.company, ['clientId', 'applicationId', 'applicantId']) as ServerCompanyCreate;

    const response = yield call(CompanyAPI.create, applicationId, company);

    yield put(createCompanySuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      company: { clientId },
    } = action;
    // abort company income & employment if create company fail
    yield all([
      put(createCompanyFail(clientId, e.response?.data || e.message)),
      put(createIncomeAbort()),
      put(updateEmploymentsAbort()),
    ]);
  }
}

export function* deleteCompanyWorker(action: DeleteCompanyAction) {
  try {
    yield waitForAuth();
    const { applicationId, clientId } = action;

    yield call(CompanyAPI.del, applicationId, clientId);

    yield put(deleteCompanySuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield put(deleteCompanyFail(clientId, e.response?.data || e.message));
  }
}

export function* fetchCompaniesWorker(action: FetchCompaniesAction): any {
  try {
    yield waitForAuth();

    const { applicationId, clientId } = action;

    let response;
    if (clientId) {
      response = yield call(CompanyAPI.fetch, applicationId, clientId);
    } else {
      response = yield call(CompanyAPI.fetchAll, applicationId);
    }

    const companies = response.data.map((company: any) =>
      omit(company, ['applicationId', 'meta', 'updatedAt', 'isDeidentified', 'isDeleted'])
    );
    yield put(fetchCompaniesSuccess(companies));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(fetchCompaniesFail(e.message));
  }
}

export function* updateCompanyWorker(action: UpdateCompanyAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;
    const companyId = yield getCompanyId(action.company.clientId, action.company.id);

    const company = omit(action.company, ['id', 'clientId', 'createdAt']) as ServerCompanyUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!companyId) {
      yield all([put(createCompany(applicationId, action.company))]);
    } else {
      yield call(CompanyAPI.update, applicationId, company, companyId);

      yield put(updateCompanySuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      company: { clientId },
    } = action;
    yield put(updateCompanyFail(clientId, e.response?.data || e.message));
  }
}

export function* upsertCompaniesWorker(action: UpsertCompaniesAction): any {
  try {
    yield waitForAuth();

    const { applicationId, companies } = action;

    const reduxStoreCompanies: Company[] = yield select((state: ReduxStoreData) => state.companies.data);
    const reduxStoreApplicants: StoreApplicant[] = yield select((state: ReduxStoreData) => state.applicants.data);
    const reduxStoreCustomer: CustomerData = yield select((state: ReduxStoreData) => state.customers?.data?.[0]);

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedCompanies = differenceWith(
      companies,
      reduxStoreCompanies,
      ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) => isEqual(rest1, rest2)
    );

    yield all(
      cloneDeep(changedCompanies).map((clientCompany) => {
        // this is for fixing CompanyOwnership for clone case
        fixCompanyOwnership(clientCompany, reduxStoreApplicants);
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreCompany = find(reduxStoreCompanies, {
          clientId: clientCompany.clientId,
        }) as Company;

        const companyWithDefaultOwners = addCompanyDefaultOwners(
          clientCompany,
          reduxStoreApplicants,
          reduxStoreCustomer
        );

        if (!reduxStoreCompany) {
          // No company
          return put(createCompany(applicationId, companyWithDefaultOwners));
        } else if (!reduxStoreCompany.id) {
          // company but no id, therefore create has been fired but success hasn't returned
          return put(updateCompany(applicationId, companyWithDefaultOwners));
        }

        // Company is in the store and has an id
        return put(updateCompany(applicationId, { id: reduxStoreCompany.id, ...companyWithDefaultOwners }));
      })
    );
    yield put(upsertCompaniesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield put(upsertCompaniesFail(e.message));
  }
}

export function* companiesWatcher() {
  yield takeEvery(REDUX_STORE.COMPANIES.ACTION_TYPES.CREATE_COMPANY, createCompanyWorker);
  yield takeEvery(REDUX_STORE.COMPANIES.ACTION_TYPES.DELETE_COMPANY, deleteCompanyWorker);
  yield takeEvery(REDUX_STORE.COMPANIES.ACTION_TYPES.FETCH_COMPANIES, fetchCompaniesWorker);
  yield takeEvery(REDUX_STORE.COMPANIES.ACTION_TYPES.UPDATE_COMPANY, updateCompanyWorker);
  yield takeEvery(REDUX_STORE.COMPANIES.ACTION_TYPES.UPSERT_COMPANIES, upsertCompaniesWorker);
}

export default companiesWatcher;
